import upperFirst from 'lodash/upperFirst';

import {
  Banner,
  Flex,
  ProgressBar,
  ProgressCircle,
  Text,
} from '@spotify-internal/encore-web';
import { DialogComposite } from '@spotify/confidence-core-react';

import { WorkflowInstance } from '../../hooks/useGetWorkflowInstance';
import { useTransitionWorkflowInstance } from '../../hooks/useTransitionWorkflowInstance';
import { useWorkflowTransitionProgress } from '../../hooks/useWorkflowTransitionProgress';
import { formatAction } from '../ActionButton';
import { WorkflowCreditGuard } from '../WorkflowCreditGuard';

import styles from './WorkflowTransitionProgressDialog.module.css';

export const WorkflowTransitionProgressDialog = ({
  workflowInstance,
  onClose,
}: {
  workflowInstance: WorkflowInstance;
  onClose: () => void;
}) => {
  const progress = useWorkflowTransitionProgress(workflowInstance);
  const { loading, performTransition } = useTransitionWorkflowInstance({
    workflowInstanceName: workflowInstance.name,
  });

  if (!progress) return null;

  const {
    action,
    currentStepDisplayName,
    completedSteps,
    totalSteps,
    failureMessage,
  } = progress;
  const actionLabel = formatAction(action);
  const stepCountLabel = `${completedSteps} of ${totalSteps} steps complete`;

  return (
    <DialogComposite.Root
      open
      width="sm"
      variant="confirmation"
      onClose={onClose}
    >
      <DialogComposite.Content className={styles.dialog}>
        <DialogComposite.Header>
          <DialogComposite.Title>
            {failureMessage
              ? `Could not ${actionLabel}`
              : `${upperFirst(actionLabel)} started`}
          </DialogComposite.Title>
        </DialogComposite.Header>
        <DialogComposite.Body>
          {failureMessage ? (
            <Banner
              colorSet="negative"
              contextual
              role="alert"
              className={styles.failureMessage}
            >
              {failureMessage}
            </Banner>
          ) : (
            <Flex direction="column" gap="base">
              <Flex alignItems="center" gap="tighter">
                <ProgressCircle
                  size="small"
                  semanticColor="textSubdued"
                  aria-label={`${upperFirst(actionLabel)} in progress`}
                  aria-hidden={totalSteps > 0}
                />
                <Text
                  variant="bodyMedium"
                  semanticColor="textSubdued"
                  role="status"
                  aria-live="polite"
                >
                  {currentStepDisplayName ?? 'Transition in progress'}
                </Text>
              </Flex>
              {totalSteps > 0 && (
                <Flex direction="column" gap="tighter-2">
                  <ProgressBar
                    value={(completedSteps / totalSteps) * 100}
                    aria-label={`${upperFirst(actionLabel)} progress`}
                    aria-valuetext={stepCountLabel}
                  />
                  <Text variant="bodySmall" semanticColor="textSubdued">
                    {stepCountLabel}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
        </DialogComposite.Body>
        <DialogComposite.Actions>
          <DialogComposite.Actions.Cancel onClick={onClose} data-track-source-anchor="786c758beb;workflow-transition-progress-dialog-cancel">
            {failureMessage ? 'Close' : 'Run in background'}
          </DialogComposite.Actions.Cancel>
          {failureMessage && (
            <WorkflowCreditGuard
              workflowInstance={workflowInstance}
              retry
              tooltip="Retry"
            >
              <DialogComposite.Actions.Primary
                isLoading={loading}
                loadingAriaLabel="Retrying"
                onClick={() => performTransition(action)}
               data-track-source-anchor="e7a6933721">
                Retry
              </DialogComposite.Actions.Primary>
            </WorkflowCreditGuard>
          )}
        </DialogComposite.Actions>
      </DialogComposite.Content>
    </DialogComposite.Root>
  );
};
